import {
  useState,
  useCallback,
  useEffect,
  useId,
  useRef,
  cloneElement,
  isValidElement,
  KeyboardEvent,
  MouseEvent,
  ReactElement,
  ReactNode,
} from 'react'
import MuiMenu from '@mui/material/Menu'
import { MenuListProps } from '@mui/material/MenuList'
import { PopoverOrigin, PopoverPosition } from '@mui/material/Popover'
import { usePageShortcuts } from '../../../contexts/AppShortcutsContext'
import { MenuIcon } from '../Icons/Icons'
import styles from './Menu.module.scss'
import {
  PAGE_ACTIONS_SHORTCUT_SECTION,
  registerPageActionsMenu,
  runMenuItemShortcut,
} from './menuShortcuts'

/**
 * Opens the menu. `withFocus` moves the keyboard into it — what a keyboard user pressing
 * ArrowDown expects, and what a pointer hover must never do to someone typing elsewhere.
 */
export type OpenMenu = (
  event: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>,
  options?: { withFocus?: boolean },
) => void

interface IProps {
  closeMenuCount?: number
  children: ReactNode
  /**
   * Renders the control the menu opens from. Receives the open handler and
   * whether the menu is currently open, so the trigger can wire its own
   * `onClick` and `aria-expanded`. Omit it to get the default three-dot icon.
   *
   * Return a single element that accepts an `id` (a Button, a link, an icon):
   * the menu labels itself from the trigger, so it stamps an `id` on whatever
   * comes back. A fragment or a bare string still renders, but carries no id,
   * and the menu then leaves `aria-labelledby` off rather than pointing it at
   * an element that is not there.
   */
  renderTrigger?: (open: OpenMenu, isOpen: boolean) => ReactNode
  /**
   * Opt in when the trigger opens on hover. MUI's Menu is a modal, so its invisible
   * backdrop otherwise covers the trigger the moment the pointer arrives and eats the
   * click that follows — the trigger's own `onClick` never runs. This lets pointer
   * events through to the page and closes the menu on pointer exit instead.
   */
  isHoverMenu?: boolean
  /** Passed straight through to MUI. Omit for MUI's default placement. */
  anchorOrigin?: PopoverOrigin
  /** Passed straight through to MUI. Omit for MUI's default placement. */
  transformOrigin?: PopoverOrigin
  /**
   * The app disables MUI's scroll lock globally (theme.ts). Pass `false` to opt
   * back in where the document itself scrolls — the popover does not follow a
   * scroll, so it would otherwise drift off its anchor.
   */
  disableScrollLock?: boolean
  /** Class for the menu's paper, for callers that style the surface. */
  paperClassName?: string
  /**
   * Keeps the popup's items mounted while the menu is closed, so reopening does
   * not rebuild them. MUI unmounts by default and every other caller relies on
   * that, so this is opt-in for the callers that already had it.
   */
  keepMounted?: boolean
  /**
   * Runs once the menu has finished closing. For an item that opens an
   * autofocused field: the overlay holds focus while it is open and restores it
   * to the trigger on the way out, and that restore arrives at the field as a
   * blur. Waiting for the exit puts the field's focus after both, rather than
   * racing them on a timer.
   */
  onExited?: () => void
  /**
   * Vetoes the dismissals MUI owns — backdrop click and Escape — for a caller
   * that must keep the menu on screen while one of its items is mid-action.
   * `closeMenuCount` deliberately still closes: it is the caller's own
   * programmatic close, so gating it too would leave a vetoing caller no way out
   * of a menu it can no longer dismiss.
   */
  disableClose?: boolean
  /**
   * Opens the menu somewhere the caller decides, instead of at a trigger.
   *
   * For a right-click menu: the sidebar rows have no button to anchor to, and the
   * menu has to appear where the pointer was, so they hold a zero-size virtual
   * element at those coordinates (see `useRowContextMenu`). Passing this puts the
   * caller in charge of opening and closing, and `renderTrigger` is then unused —
   * there is no trigger. Every other caller is unaffected.
   */
  controlledAnchorEl?: HTMLElement | null
  /**
   * Opens the menu at a point on screen — a right-click menu with no element to anchor
   * to. Controlled like `controlledAnchorEl`: `null` is closed, and there is no trigger,
   * so the list carries no `aria-labelledby`.
   */
  anchorPosition?: PopoverPosition | null
  /** Required with `controlledAnchorEl` or `anchorPosition` — the caller owns dismissal too. */
  onClose?: () => void
  /** Merged onto the list (e.g. `dense` for compact rows); the trigger label is kept. */
  menuListProps?: Partial<MenuListProps>
  /**
   * Lets pointer events through the modal root to the page, as hover mode does, for a
   * caller that dismisses the menu on an outside click of its own.
   */
  isNonBlocking?: boolean
  /** Passed straight through to MUI, for a menu that must leave focus where it is. */
  disableAutoFocus?: boolean
  disableEnforceFocus?: boolean
  disableRestoreFocus?: boolean
  /**
   * The page's own actions menu, in its header: the `.` key opens it from anywhere on the
   * page. Needs an id-capable trigger to anchor to.
   */
  isPageActions?: boolean
  /** Forwarded to the menu root, e.g. `data-cy` for Cypress. */
  [dataAttribute: `data-${string}`]: string | undefined
}

interface ITriggerProps {
  id?: string
  onMouseLeave?: () => void
}

// `isValidElement` is also true for React's built-in wrappers — Fragment,
// Suspense, StrictMode — which render no host element and accept no props
// beyond `key`/`children`. Cloning one drops the `id` on the floor, which would
// leave the menu labelled by an element that never reaches the page. They are
// exactly the element types identified by a symbol, so screen them out here.
const isIdCapableElement = (trigger: ReactNode): trigger is ReactElement<ITriggerProps> =>
  isValidElement<ITriggerProps>(trigger) && typeof trigger.type !== 'symbol'

// The menu labels itself from its trigger, so the trigger needs a real id in
// the page. Reuse the caller's own id when it set one, otherwise stamp ours on.
// In hover mode the exit handler rides along here too, so a caller only has to
// wire the open handler it already gets.
const withTriggerProps = (
  trigger: ReactNode,
  fallbackId: string,
  hoverProps: ITriggerProps,
): { trigger: ReactNode; triggerId: string | undefined } => {
  if (!isIdCapableElement(trigger)) {
    return { trigger, triggerId: undefined }
  }
  const triggerId = trigger.props.id ?? fallbackId

  return { trigger: cloneElement(trigger, { id: triggerId, ...hoverProps }), triggerId }
}

/** Lists the page actions key on the `?` shortcut list for as long as the menu is mounted. */
const PageActionsShortcutListing = (): null => {
  usePageShortcuts(PAGE_ACTIONS_SHORTCUT_SECTION)
  return null
}

/** Long enough to cross the gap between trigger and popup, short enough not to linger. */
const HOVER_CLOSE_GRACE_MS = 150

/** Set by `Common/MenuItem`'s `keepMenuOpen`; read here because raw MUI items share the list. */
export const KEEP_MENU_OPEN_ATTRIBUTE = 'data-keep-menu-open'

// The menu sits on the popover layer, above modals, so an item that opens a modal must close
// it or the menu's backdrop swallows every click in that modal. Only items physically inside
// this list count: React bubbles through portals, so a modal or Select popup rendered from an
// item would otherwise close the menu too.
const isClosingItemActivation = (target: EventTarget, list: HTMLElement): boolean => {
  const item = target instanceof Element ? target.closest('[role="menuitem"]') : null
  return !!item && list.contains(item) && !item.hasAttribute(KEEP_MENU_OPEN_ATTRIBUTE)
}

const Menu = ({
  children,
  closeMenuCount,
  renderTrigger,
  isHoverMenu,
  anchorOrigin,
  transformOrigin,
  disableScrollLock,
  paperClassName,
  keepMounted,
  onExited,
  disableClose,
  controlledAnchorEl,
  anchorPosition,
  onClose,
  menuListProps,
  isNonBlocking,
  disableAutoFocus,
  disableEnforceFocus,
  disableRestoreFocus,
  isPageActions,
  ...dataAttributes
}: IProps) => {
  const [triggerAnchorEl, setTriggerAnchorEl] = useState<HTMLElement | null>(null)
  const isPositioned = anchorPosition !== undefined
  const isControlled = isPositioned || controlledAnchorEl !== undefined
  const anchorEl = isControlled ? (controlledAnchorEl ?? null) : triggerAnchorEl
  const [shouldFocusOnOpen, setShouldFocusOnOpen] = useState(false)
  const instanceId = useId()
  const defaultTriggerId = `menu-trigger-${instanceId}`

  const closeMenu = useCallback(() => {
    if (isControlled) {
      onClose?.()
      return
    }
    setTriggerAnchorEl(null)
  }, [isControlled, onClose])

  const handleCloseMenu = useCallback(() => {
    if (disableClose) {
      return
    }
    closeMenu()
  }, [disableClose, closeMenu])

  // Nothing intercepts an outside click in hover mode — that is the whole point — so the
  // pointer leaving both trigger and popup is what closes the menu. The grace period
  // covers the gap the pointer crosses between the two.
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const cancelScheduledClose = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
  }, [])

  const scheduleClose = useCallback(() => {
    cancelScheduledClose()
    closeTimer.current = setTimeout(handleCloseMenu, HOVER_CLOSE_GRACE_MS)
  }, [cancelScheduledClose, handleCloseMenu])

  useEffect(() => cancelScheduledClose, [cancelScheduledClose])

  const handleOpenMenu = useCallback<OpenMenu>(
    (event, { withFocus = false } = {}) => {
      cancelScheduledClose()
      setShouldFocusOnOpen(withFocus)
      setTriggerAnchorEl(event.currentTarget)
    },
    [cancelScheduledClose],
  )

  useEffect(() => {
    if (closeMenuCount) {
      closeMenu()
    }
  }, [closeMenuCount, closeMenu])

  const isOpen = isPositioned ? !!anchorPosition : !!anchorEl
  const isPointerPassThrough = isHoverMenu || isNonBlocking

  const hoverProps: ITriggerProps = isHoverMenu ? { onMouseLeave: scheduleClose } : {}

  // A controlled menu is opened by something that is not a trigger — a right-click
  // on the row or cell itself — so there is no element to render or to label it from.
  const { trigger, triggerId } = isControlled
    ? { trigger: null, triggerId: undefined }
    : renderTrigger
      ? withTriggerProps(renderTrigger(handleOpenMenu, isOpen), defaultTriggerId, hoverProps)
      : {
          trigger: <MenuIcon id={defaultTriggerId} onClick={handleOpenMenu} />,
          triggerId: defaultTriggerId,
        }

  useEffect(() => {
    if (!isPageActions || !triggerId) return
    return registerPageActionsMenu(() => {
      const triggerElement = document.getElementById(triggerId)
      if (triggerElement) setTriggerAnchorEl(triggerElement)
    })
  }, [isPageActions, triggerId])

  const callerListKeyDown = menuListProps?.onKeyDown
  const handleListKeyDown = useCallback(
    (event: KeyboardEvent<HTMLUListElement>) => {
      callerListKeyDown?.(event)
      if (runMenuItemShortcut(event)) event.preventDefault()
      // MUI runs an item's onClick on Enter keydown and Space keyup without a DOM click.
      if (event.key === 'Enter' && isClosingItemActivation(event.target, event.currentTarget)) {
        handleCloseMenu()
      }
    },
    [callerListKeyDown, handleCloseMenu],
  )

  const callerListKeyUp = menuListProps?.onKeyUp
  const handleListKeyUp = useCallback(
    (event: KeyboardEvent<HTMLUListElement>) => {
      callerListKeyUp?.(event)
      if (event.key === ' ' && isClosingItemActivation(event.target, event.currentTarget)) {
        handleCloseMenu()
      }
    },
    [callerListKeyUp, handleCloseMenu],
  )

  const callerListClick = menuListProps?.onClick
  const handleListClick = useCallback(
    (event: MouseEvent<HTMLUListElement>) => {
      callerListClick?.(event)
      if (isClosingItemActivation(event.target, event.currentTarget)) handleCloseMenu()
    },
    [callerListClick, handleCloseMenu],
  )

  return (
    <>
      {trigger}
      {isPageActions && <PageActionsShortcutListing />}
      <MuiMenu
        {...dataAttributes}
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleCloseMenu}
        // Kept while closing too, so the exit transition does not fall back to `anchorEl`.
        {...(isPositioned && {
          anchorReference: 'anchorPosition' as const,
          anchorPosition: anchorPosition ?? undefined,
        })}
        // Spread conditionally: MUI's Menu applies its own origin defaults and
        // then spreads the caller's props over them, so passing an explicit
        // `undefined` would fall back to Popover's defaults and move the menu.
        // `disableScrollLock` is spread the same way so an omitted prop leaves
        // theme.ts's app-wide `MuiMenu` default in place — and `false` is a
        // value it has to be able to carry, hence the explicit undefined check.
        {...(anchorOrigin && { anchorOrigin })}
        {...(transformOrigin && { transformOrigin })}
        {...(disableScrollLock !== undefined && { disableScrollLock })}
        {...(paperClassName && { classes: { paper: paperClassName } })}
        {...(keepMounted && { keepMounted })}
        {...(onExited && { TransitionProps: { onExited } })}
        {...(isPointerPassThrough && {
          // The modal root is what covers the page; letting pointer events through it is
          // what puts the trigger's own click back within reach. The popup re-enables
          // them so its items stay clickable.
          slotProps: {
            root: { className: styles.passThroughRoot },
            paper: {
              className: styles.passThroughPaper,
              ...(isHoverMenu && {
                onMouseEnter: cancelScheduledClose,
                onMouseLeave: scheduleClose,
              }),
            },
          },
        })}
        {...(isHoverMenu && {
          // MUI focuses the list on open. Right for a menu someone asked for with the
          // keyboard, wrong for one a passing pointer opened — that yanks the caret out of
          // whatever the user was typing. So focus follows how it was opened, and a hover
          // menu also keeps its hands off focus on the way out.
          autoFocus: shouldFocusOnOpen,
          // `autoFocus` governs the list; the modal focuses its own paper independently.
          disableAutoFocus: !shouldFocusOnOpen,
          disableAutoFocusItem: !shouldFocusOnOpen,
          disableEnforceFocus: !shouldFocusOnOpen,
          disableRestoreFocus: !shouldFocusOnOpen,
        })}
        {...(disableAutoFocus && { disableAutoFocus })}
        {...(disableEnforceFocus && { disableEnforceFocus })}
        {...(disableRestoreFocus && { disableRestoreFocus })}
        MenuListProps={{
          ...menuListProps,
          onKeyDown: handleListKeyDown,
          onKeyUp: handleListKeyUp,
          onClick: handleListClick,
          'aria-labelledby': triggerId,
        }}
      >
        {children}
      </MuiMenu>
    </>
  )
}

export default Menu
